import Vue from 'vue';
import modal from './components/Modal/Modal';
import config from 'config';
import RequestService from './services/RequestService';
import { assets } from './Assets';
import VisibilityService from './services/VisibilityService';
import { HandleInteractionService, InteractionBlockTypes } from './services/HandleInteractionService';
import LayoutAssistedSuggestHooks from 'LayoutAssistedSuggestHooks';
import HandleShopInteractionService from './services/HandleShopInteractionService';
import { InlineSvgPlugin } from 'vue-inline-svg';
import ComponentUtils from './services/ComponentUtils';
import { LandingPageMatchTypes } from './components/SmartSuggestBlocks/Landingpage';

class LayoutAssistedSuggest {
    private readonly assistedSuggestCssClass = 'fl-layout-assisted-suggest';
    private handleShopInteractionService: HandleShopInteractionService;
    private handleInteractionService: HandleInteractionService;
    private requestService: RequestService;
    private isLoaded = false;
    private vueEntry: Vue;

    private injectAssistedSuggestElement(): void {
        const element = document.createElement('div');
        element.classList.add(this.assistedSuggestCssClass);

        document.body.appendChild(element);
    }

    /**
     * Some shops initialize findologic twice
     * or rearrange their whole DOM and findologic automatically re-initializes
     * In that care we delete the old assisted suggest fields and let the js garbage collector clean up everything else
     */
    private removeAssistedSuggestInstance(): void {
        const element = document.getElementsByClassName('fl-assisted-suggest');
        if (element[0]) {
            document.body.removeChild(element[0]);
        }

        if (this.vueEntry) {
            this.vueEntry.$destroy();
        }
    }

    private isQueryLongEnoughToShowResults(query: string = ''): boolean {
        return query.length >= config.assistedSuggest.config.queryLengthToShowResults;
    }

    private handleShopEvents(): void {
        this.handleShopInteractionService.onInputTriggered((input: HTMLInputElement) => {
            const queryLongEnough = this.isQueryLongEnoughToShowResults(input.value);

            // initial click/focus
            if (!this.isLoaded) {
                this.initVue();
                if (!queryLongEnough) {
                    VisibilityService.hide();
                }

                return;
            }

            if (!queryLongEnough) {
                return;
            }

            if (!this.requestService.isQuerySameAsLastOne(input.value) || config.assistedSuggest.config.ignoreCacheOnInputTriggered) {
                this.requestService.updateResultSubscribers(input.value);
            } else {
                VisibilityService.show();
            }
        });

        this.handleShopInteractionService.onInputChanged((value: string) => {
            if (this.isQueryLongEnoughToShowResults(value)) {
                this.requestService.updateResultSubscribers(value);
            } else {
                VisibilityService.hide();
            }
        });

        this.handleShopInteractionService.onInputSubmitted((event: Event) => {
            this.requestService.abortAllActiveRequests();

            const currentInputValue = this.handleShopInteractionService.getActiveInputValue();
            const isSubmittedInputFullyMatchingLandingPage =
                this.requestService.getLastResponse()?.landingpage?.matchType === LandingPageMatchTypes.full;

            if (isSubmittedInputFullyMatchingLandingPage) {
                event.preventDefault();
                const matchingLandingPage = this.requestService.getLastResponse().landingpage;
                void this.handleInteractionService
                    .byBlock(InteractionBlockTypes.landingpage)
                    .withUrl(matchingLandingPage.name, matchingLandingPage.url)
                    .trackAndSubmit();
            } else if (
                config.assistedSuggest.config.autocompleteQueryOnSubmit &&
                this.requestService.isQuerySameAsLastCompletedOne(currentInputValue)
            ) {
                // replace the active input when the hooks allow it and there is no active request
                // which would invalidate the current effectiveProductSuggestionQuery
                this.handleShopInteractionService.setActiveInputValue(
                    this.requestService.getMostRecentResponseWithProducts().effectiveProductSuggestionQuery
                );
            }

            VisibilityService.hide();
        });
        this.handleShopInteractionService.onInputAutocompleted((inputElement: HTMLInputElement) => {
            const lastEffectiveProductSuggestionQuery = this.requestService.getLastResponse()?.effectiveProductSuggestionQuery;

            if (lastEffectiveProductSuggestionQuery && lastEffectiveProductSuggestionQuery.startsWith(inputElement.value)) {
                inputElement.value = lastEffectiveProductSuggestionQuery;
            }
        });
    }

    private handleAssistedSuggestEvents(): void {
        this.requestService.onResultChangeWithFallback(() => {
            if (this.isQueryLongEnoughToShowResults(this.handleShopInteractionService.getActiveInputValue())) {
                VisibilityService.show();
            }
        });
    }

    private initVue(): void {
        this.removeAssistedSuggestInstance();

        this.injectAssistedSuggestElement();
        Vue.use(InlineSvgPlugin);

        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const layoutAssistedSuggestContext = this;
        this.vueEntry = new Vue({
            template: `
        <fl-modal />`,
            el: '.fl-layout-assisted-suggest',
            provide(): Record<string, any> {
                return {
                    config,
                    requestService: layoutAssistedSuggestContext.requestService,
                    assets,
                    HandleInteractionService: layoutAssistedSuggestContext.handleInteractionService,
                    HandleShopInteractionService: layoutAssistedSuggestContext.handleShopInteractionService,
                    utils: new ComponentUtils(),
                };
            },
            mounted(): void {
                // Every sub component should be ready therefore we use $nextTick -> https://vuejs.org/v2/api/#mounted
                this.$nextTick(() => {
                    const currentQuery = layoutAssistedSuggestContext.handleShopInteractionService.getActiveInputValue();
                    layoutAssistedSuggestContext.requestService.updateResultSubscribers(currentQuery);
                    LayoutAssistedSuggestHooks.created();
                });
            },
            components: {
                Modal: modal,
            },
        });

        this.isLoaded = true;
    }

    public init(): void {
        this.isLoaded = false;
        LayoutAssistedSuggestHooks.beforeCreate(Vue);

        this.requestService = new RequestService();
        this.handleShopInteractionService = new HandleShopInteractionService();
        this.handleInteractionService = new HandleInteractionService(this.handleShopInteractionService);

        this.handleAssistedSuggestEvents();
        this.handleShopEvents();
    }
}

const layoutAssistedSuggest = new LayoutAssistedSuggest();
export default layoutAssistedSuggest;
